import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface BirthData {
  name:      string;
  birthDate: string;
  birthTime: string;
  birthCity: string;
}

export function SynastryInvitePublic() {
  const [params]      = useSearchParams();
  const token         = params.get('token') ?? '';
  const [form, setForm]     = useState<BirthData>({ name: '', birthDate: '', birthTime: '', birthCity: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');

  function update<K extends keyof BirthData>(key: K, value: string) {
    setForm(f => ({ ...f, [key]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) { setErrMsg('Link de convite inválido.'); setStatus('error'); return; }

    setStatus('submitting');

    const { error } = await supabase
      .from('synastry_invites')
      .update({
        invitee_name:       form.name,
        invitee_birth_data: {
          birth_date: form.birthDate,
          birth_time: form.birthTime,
          birth_city: form.birthCity,
        },
        status: 'completed',
      })
      .eq('token', token)
      .eq('status', 'pending');

    if (error) {
      setErrMsg('Não foi possível enviar. O convite pode ter expirado.');
      setStatus('error');
    } else {
      setStatus('done');
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0D1130] flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-[#C9A84C] text-lg">Link de convite inválido.</p>
          <p className="text-[#F5ECD7]/60 text-sm mt-2">Peça um novo link à pessoa que te convidou.</p>
        </div>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div className="min-h-screen bg-[#0D1130] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-[#C9A84C] text-3xl tracking-[8px] mb-4">✦</div>
          <h1 className="text-[#C9A84C] text-2xl font-light mb-4">Dados enviados com sucesso</h1>
          <p className="text-[#F5ECD7]/80 max-w-sm">
            A sinastria será gerada em breve. Você pode fechar esta janela.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D1130] py-12 px-4">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="text-[#C9A84C] text-2xl tracking-[8px] mb-2" aria-hidden="true">✦</div>
          <h1 className="text-[#C9A84C] text-2xl font-light mb-2">Convite de Sinastria</h1>
          <p className="text-[#F5ECD7]/70 text-sm">
            Preencha seus dados de nascimento para que a sinastria seja gerada.
          </p>
        </div>

        {status === 'error' && (
          <div className="mb-6 p-4 border border-red-500/40 bg-red-900/20 text-red-300 text-sm rounded-sm">
            {errMsg}
          </div>
        )}

        <form onSubmit={submit} className="flex flex-col gap-5">
          {([
            { key: 'name',      label: 'Seu nome',             type: 'text',  placeholder: 'Como você gosta de ser chamada', required: true },
            { key: 'birthDate', label: 'Data de nascimento',   type: 'date',  placeholder: '',                                required: true },
            { key: 'birthTime', label: 'Hora de nascimento',   type: 'time',  placeholder: 'Aproximada está ótimo',           required: false },
            { key: 'birthCity', label: 'Cidade de nascimento', type: 'text',  placeholder: 'Cidade, Estado',                  required: true },
          ] as const).map(({ key, label, type, placeholder, required }) => (
            <label key={key} className="block">
              <span className="block text-[#F5ECD7]/70 text-xs uppercase tracking-wider mb-2">{label}</span>
              <input
                type={type}
                value={form[key]}
                onChange={e => update(key, e.target.value)}
                placeholder={placeholder}
                required={required}
                className="w-full bg-transparent border border-[#C9A84C]/30 text-[#F5ECD7] p-3 rounded-sm placeholder-[#F5ECD7]/30 focus:border-[#C9A84C] focus:outline-none"
                aria-label={label}
              />
            </label>
          ))}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="mt-2 px-8 py-4 bg-[#C9A84C] text-[#0D1130] tracking-[2px] uppercase text-xs font-bold hover:bg-[#C9A84C]/90 disabled:opacity-60 transition-colors"
          >
            {status === 'submitting' ? 'Enviando…' : 'Enviar dados para sinastria'}
          </button>
        </form>

        <p className="text-center text-[#F5ECD7]/30 text-xs mt-6">
          Seus dados serão usados apenas para gerar a sinastria solicitada.
        </p>
      </div>
    </div>
  );
}
